import { Accordion } from '@chakra-ui/react';
import { getOperatingCostsData } from '~/lib/homes/profile/operatingCosts';
import { getRoomsData } from '~/lib/homes/profile/rooms';
import { isFarm, NestedHome } from '@habity/types';
import { isCommercialProperty } from '@habity/types/vitec';
import { AccordionTabItem } from '../../../../ui/tabs/AccordionTabItem';
import { ProfileTable } from '../../../../ui/tables/Table';
import { getBuildingData } from '~/lib/homes/profile/building';
import { Documents } from './Documents';
import { getPropertyData } from '~/lib/homes/profile/property';
import { getRenovationsData } from '~/lib/homes/profile/renovations';
import { ParagraphList } from '../../../../ui/tables/ParagraphList';
import { ProspectBody } from './Prospect';
import { getProspect, hasProspect } from '~/lib/homes/profile/prospect';
import { ProfileModalType } from '@habity/types';
import { useTranslations } from 'next-intl';
import { getAssociationData } from '~/lib/homes/profile/association';
import { getEconomyBuildingsData } from '~/lib/homes/profile/economyBuildings';
import { getLandsData } from '~/lib/homes/profile/lands';
import {
  getBuildingDataForCommercialBuilding,
  getRoomsDataForCommercialBuilding,
} from '~/lib/homes/profile/commercialBuilding';
import { CompilationAreasTable } from '../../../../ui/tables/CompilationAreasTable';
import { hasCompilationAreasData } from '~/lib/homes/profile/commercialAreasFields';
import { capitalizeFirstLetter } from '@habity/utils';
import {
  getBuildingDataForFarm,
  getRoomsDataForFarmResidentialBuilding,
} from '~/lib/homes/profile/farm';
import { FarmInfoEstateOtherResidentialBuilding } from '@habity/types/vitec/common/farmInfoEstateOtherResidentialBuilding';
import { getBalconyPatioData } from '~/lib/homes/profile/balconyPatio';

/**
 * Helper function to get a proper building name for commercial properties
 */
const getCommercialOrFarmBuildingName = (
  building: { name?: string },
  index: number
): string => {
  if (!building.name) {
    return `Byggnad ${index + 1}`;
  }

  // Check if the name is just a number (like '1', '2', '3')
  const trimmedName = building.name.trim();
  if (/^\d+$/.test(trimmedName)) {
    return `Byggnad ${trimmedName}`;
  }

  return building.name;
};

interface AccordionDetailedProps {
  home: NestedHome;
  handleOpen: (params: ProfileModalType) => void;
}

export const AccordionDetailed = ({
  home,
  handleOpen,
}: AccordionDetailedProps) => {
  const { provider_data } = home || {};
  const { advertiseOn } = provider_data || {};
  const { documents } = advertiseOn || {};
  const t = useTranslations('HomeProfile.Accordions');

  const prospect = getProspect(home);
  const propertyData = getPropertyData(home);
  const associationData = getAssociationData(home);
  const buildingData = getBuildingData(home);
  const roomsData = getRoomsData(home);
  const renovationsData = getRenovationsData(home);
  const economyBuildingsData = getEconomyBuildingsData(home);
  const landsData = getLandsData(home);
  const operatingCostsData = getOperatingCostsData(home);
  const balconyPatioData = getBalconyPatioData(home);

  return (
    <Accordion
      id="home-profile-details"
      borderTopWidth="1px"
      borderTopColor="blackAlpha.500"
      allowMultiple
    >
      {hasProspect(home) && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('Prospect.title')}
          subtitle={''}
          variant="profile"
        >
          <ProspectBody prospect={prospect} />
        </AccordionTabItem>
      )}

      {propertyData.length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('Property.title')}
          subtitle={''}
          variant="profile"
        >
          <ProfileTable data={propertyData} />
        </AccordionTabItem>
      )}

      {associationData.length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('Association.title')}
          subtitle={''}
          variant="profile"
        >
          <ProfileTable data={associationData} />
        </AccordionTabItem>
      )}

      {balconyPatioData.length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('BalconyPatio.title')}
          subtitle={''}
          variant="profile"
        >
          <ProfileTable data={balconyPatioData} />
        </AccordionTabItem>
      )}

      {buildingData.length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={
            home.provider_data &&
            home.provider_type &&
            isFarm(home.provider_data, home.provider_type)
              ? t('MainBuilding.title')
              : t('Building.title')
          }
          subtitle={''}
          variant="profile"
        >
          <ProfileTable collapseAfter={10} data={buildingData} />
        </AccordionTabItem>
      )}

      {roomsData.length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('Rooms.title')}
          subtitle={''}
          variant="profile"
        >
          <ParagraphList data={roomsData} />
        </AccordionTabItem>
      )}

      {renovationsData.length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('Renovations.title')}
          subtitle={''}
          variant="profile"
        >
          <ParagraphList data={renovationsData} />
        </AccordionTabItem>
      )}

      {/* {getBuildingsData(home).length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('Buildings.title')}
          subtitle={''}
          variant="profile"
        >
          <ProfileTable collapseAfter={10} data={getBuildingsData(home)} />
        </AccordionTabItem>
      )} */}

      {economyBuildingsData.length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('EconomyBuildings.title')}
          subtitle={''}
          variant="profile"
        >
          <ProfileTable collapseAfter={10} data={economyBuildingsData} />
        </AccordionTabItem>
      )}

      {landsData.length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('Lands.title')}
          subtitle={''}
          variant="profile"
        >
          <ProfileTable collapseAfter={10} data={landsData} />
        </AccordionTabItem>
      )}
      {/*
      {getSurroundingData(home).length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('Surrounding.title')}
          subtitle={''}
          variant="profile"
        >
          <ProfileTable data={getSurroundingData(home)} />
        </AccordionTabItem>
      )} */}

      {/* Commercial Property Buildings */}
      {home.provider_data &&
        home.provider_type &&
        isCommercialProperty(home.provider_data, home.provider_type) &&
        home.provider_data.buildings &&
        home.provider_data.buildings.length > 0 && (
          <AccordionTabItem
            panelProps={{ px: 0, pl: 3 }}
            title={t('Buildings.title')}
            subtitle={''}
            variant="profile"
          >
            <Accordion id="commercial-buildings" allowMultiple>
              {home.provider_data.buildings.map((building, index) => (
                <AccordionTabItem
                  key={building.id || index}
                  panelProps={{ px: 0, pl: 6 }}
                  title={capitalizeFirstLetter(
                    getCommercialOrFarmBuildingName(building, index),
                    true
                  )}
                  subtitle={building.buildingType || ''}
                  variant="profile"
                >
                  <Accordion
                    id={`commercial-building-${building.id || index}`}
                    allowMultiple
                  >
                    {/* Building Data Subaccordion */}
                    <AccordionTabItem
                      panelProps={{ px: 0 }}
                      title="Byggnadsdata"
                      subtitle={''}
                      variant="profile"
                    >
                      <ProfileTable
                        data={getBuildingDataForCommercialBuilding(building)}
                        collapseAfter={10}
                      />
                    </AccordionTabItem>

                    {/* Rooms Data Subaccordion */}
                    {building.rooms && building.rooms.length > 0 && (
                      <AccordionTabItem
                        panelProps={{ px: 0 }}
                        title="Rumsbeskrivningar"
                        subtitle={''}
                        variant="profile"
                      >
                        <ProfileTable
                          data={getRoomsDataForCommercialBuilding(building)}
                        />
                      </AccordionTabItem>
                    )}
                  </Accordion>
                </AccordionTabItem>
              ))}
            </Accordion>
          </AccordionTabItem>
        )}

      {/* Commercial Property Areas */}
      {home.provider_data &&
        home.provider_type &&
        isCommercialProperty(home.provider_data, home.provider_type) &&
        home.provider_data.compilationAreas &&
        hasCompilationAreasData(home.provider_data.compilationAreas) && (
          <AccordionTabItem
            panelProps={{ px: 0 }}
            title="Areor"
            subtitle={''}
            variant="profile"
          >
            <CompilationAreasTable
              compilationAreas={home.provider_data.compilationAreas}
            />
          </AccordionTabItem>
        )}

      {/* Farm other residential buildings */}
      {home.provider_data &&
        home.provider_type &&
        isFarm(home.provider_data, home.provider_type) &&
        home.provider_data.otherResidentialBuildings &&
        home.provider_data.otherResidentialBuildings.length > 0 && (
          <AccordionTabItem
            panelProps={{ px: 0, pl: 3 }}
            title={t('Buildings.title')}
            subtitle={''}
            variant="profile"
          >
            <Accordion id="farm-buildings" allowMultiple>
              {home.provider_data.otherResidentialBuildings.map(
                (building, index) => {
                  const hasRooms = building.rooms && building.rooms.length > 0;

                  return (
                    <AccordionTabItem
                      key={building.id || index}
                      panelProps={{ px: 0, pl: hasRooms ? 6 : 0 }}
                      title={capitalizeFirstLetter(
                        getCommercialOrFarmBuildingName(building, index),
                        true
                      )}
                      subtitle={building.buildingType || ''}
                      variant="profile"
                    >
                      {/* If only Byggnadsdata exists, show it directly without nested accordion */}
                      {!hasRooms ? (
                        <ProfileTable
                          data={getBuildingDataForFarm(building)}
                          collapseAfter={10}
                        />
                      ) : (
                        /* If multiple tabs exist, use nested accordion */
                        <Accordion
                          id={`farm-building-${building.id || index}`}
                          allowMultiple
                        >
                          {/* Building Data Subaccordion */}
                          <AccordionTabItem
                            panelProps={{ px: 0 }}
                            title="Byggnadsdata"
                            subtitle={''}
                            variant="profile"
                            hasUnderline={false}
                          >
                            <ProfileTable
                              data={getBuildingDataForFarm(building)}
                              collapseAfter={10}
                            />
                          </AccordionTabItem>

                          {/* Rooms Data Subaccordion */}
                          <AccordionTabItem
                            panelProps={{ px: 0 }}
                            title="Rumsbeskrivningar"
                            subtitle={''}
                            variant="profile"
                            hasUnderline={false}
                          >
                            <ProfileTable
                              data={getRoomsDataForFarmResidentialBuilding(
                                building
                              )}
                            />
                          </AccordionTabItem>
                        </Accordion>
                      )}
                    </AccordionTabItem>
                  );
                }
              )}
            </Accordion>
          </AccordionTabItem>
        )}

      {operatingCostsData.length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('OperatingCosts.title')}
          subtitle={''}
          variant="profile"
        >
          <ProfileTable data={operatingCostsData} />
        </AccordionTabItem>
      )}

      {documents && documents.length > 0 && (
        <AccordionTabItem
          panelProps={{ px: 0 }}
          title={t('Documents.title')}
          subtitle={''}
          variant="profile"
        >
          <Documents documents={documents} />
        </AccordionTabItem>
      )}
    </Accordion>
  );
};
